import type { FC } from "react";
import { useMemo } from "react";

import dayjs from "dayjs";
import type { Point } from "highcharts";

import { shortMonthDayAndYearWithDayOfWeek } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageFilter, abbrNumberFilter } from "app/filters/ngFilters";

import type { ITooltipColumnDefinition, SeriesLineItem } from "./types";

interface IInfluenceGraphTooltip {
    data: Array<SeriesLineItem>;
    context: Point;
}

export const InfluenceGraphTooltip: FC<IInfluenceGraphTooltip> = ({ data, context }) => {
    const rows = useMemo(
        () =>
            data
                .filter((item) => item.visible)
                .map((rawData) => {
                    const visibilityPoint = rawData.data.find((p) => p[0] === context.x);
                    const urlPoint = rawData.urlData.find((p) => p[0] === context.x);

                    return {
                        displayName: rawData.name,
                        fieldValue: visibilityPoint?.[1] ?? 0,
                        originalValue: visibilityPoint?.[1] ?? 0,
                        urlCount: urlPoint?.[1] ?? 0,
                        id: rawData.id,
                        isChecked: rawData.visible,
                        items: {
                            label: rawData.name,
                            color: rawData.color,
                        },
                        urlData: rawData.urlData,
                    };
                })
                .sort((a, b) => b.originalValue - a.originalValue),
        [data, context.x],
    );

    const columnDefinition: Array<ColumnDefinition<ITooltipColumnDefinition>> = [
        {
            key: "items",
            title: translate("llm.sources.influence.graph.legend.header.domain"),
            selector: "items",
            align: "left",
            size: "175px",
            render: ({ items }: ITooltipColumnDefinition) => (
                <ChartTooltip.LabelCell
                    label={items.label}
                    backgroundColor={items.color}
                />
            ),
        },
        {
            key: "fieldValue",
            title: translate("llm.sources.influence.graph.legend.header.influence"),
            selector: "fieldValue",
            align: "right",
            size: "75px",
            render: ({ fieldValue }: ITooltipColumnDefinition) => (
                <ChartTooltip.ValueCell value={percentageFilter()(fieldValue, 1)} />
            ),
        },
        {
            key: "urlCount",
            title: translate("llm.sources.influence.graph.legend.header.url-count"),
            selector: "urlCount",
            align: "right",
            size: "75px",
            render: ({ urlCount }: ITooltipColumnDefinition) => (
                <ChartTooltip.ValueCell value={abbrNumberFilter()(urlCount)} />
            ),
        },
    ];

    const tooltipTitle = dayjs.utc(context.key).format(shortMonthDayAndYearWithDayOfWeek[0]);
    return (
        <Flex width="370px">
            <ChartTooltip
                width={370}
                title={tooltipTitle}
                columnDefinition={columnDefinition}
                data={rows}
            />
        </Flex>
    );
};
